import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../apiConfig';
import './SocialComponents.css';

const LikeButton = ({ 
  itemId, 
  itemType, 
  initialLiked = false, 
  initialCount = 0,
  size = 'medium',
  showCount = true,
  className = '',
  onLikeChange
}) => {
  const { currentUser } = useAuth();
  const [liked, setLiked] = useState(initialLiked);
  const [count, setCount] = useState(initialCount);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    setLiked(initialLiked);
    setCount(initialCount);
  }, [initialLiked, initialCount]);

  // Real-time updates via Socket.io
  useEffect(() => {
    if (window.socket) {
      const handleLikeUpdate = (data) => {
        if (data.itemId === itemId && data.itemType === itemType) {
          setCount(data.likesCount);
          // Don't update liked state from socket - only from user actions
        }
      };

      window.socket.on('like_updated', handleLikeUpdate);
      
      return () => {
        window.socket.off('like_updated', handleLikeUpdate);
      };
    }
  }, [itemId, itemType]);

  const handleLike = async () => {
    if (!currentUser) {
      // Could show login modal here
      return;
    }

    if (isLoading) return;

    // Optimistic UI update
    const newLiked = !liked;
    const newCount = newLiked ? count + 1 : count - 1;
    
    setLiked(newLiked);
    setCount(newCount);
    setIsLoading(true);
    setIsAnimating(true);

    // Trigger animation
    setTimeout(() => setIsAnimating(false), 300);

    try {
      const response = await api.post('/social/like', {
        itemId,
        itemType
      });

      // Update with server response
      setLiked(response.data.liked);
      setCount(response.data.likesCount);

      // Call callback if provided
      if (onLikeChange) {
        onLikeChange(response.data.liked, response.data.likesCount);
      }

      // Emit Socket.io event for real-time updates
      if (window.socket) {
        window.socket.emit(newLiked ? 'like_added' : 'like_removed', {
          itemId,
          itemType,
          likesCount: response.data.likesCount
        });
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      
      // Revert optimistic update on error
      setLiked(!newLiked);
      setCount(newLiked ? count - 1 : count + 1);
    } finally {
      setIsLoading(false);
    }
  };

  const sizeClass = `like-button--${size}`;
  const stateClass = liked ? 'like-button--liked' : 'like-button--unliked';
  const loadingClass = isLoading ? 'like-button--loading' : '';
  const animatingClass = isAnimating ? 'like-button--animating' : '';

  return (
    <button
      className={`like-button ${sizeClass} ${stateClass} ${loadingClass} ${animatingClass} ${className}`}
      onClick={handleLike}
      disabled={!currentUser || isLoading}
      aria-label={liked ? `Unlike this ${itemType}` : `Like this ${itemType}`}
      aria-pressed={liked}
      title={liked ? 'Unlike' : 'Like'}
    >
      <span className="like-button__icon" aria-hidden="true">
        {liked ? '❤️' : '🤍'}
      </span>
      {showCount && (
        <span className="like-button__count" aria-label={`${count} likes`}>
          {count > 0 ? count : ''}
        </span>
      )}
      {isLoading && <span className="like-button__spinner" aria-hidden="true"></span>}
    </button>
  );
};

export default LikeButton; 